import { DEFAULT_PAGE_SIZE } from '@w7/ui'
import { useQuery } from '@tanstack/react-query'
import { apiFetch, hasLiveApi } from '@/api/client'
import { isUnreachableApiError } from '@/api/unreachable'
import {
  fromDownlineAccountsData,
  fromFixturePnl,
  netFromDownlineSumOnly,
  plStatementRangeError,
  unavailablePlStatement,
  type GetDownlineAccountsDataResponse,
  type GetDownlineAccountsSumOnlyResponse,
  type PlStatementQuery,
  type PlStatementResult,
} from '@/api/map/plStatement'
import * as api from '@/data/api'
import { FIXTURE_GAMES } from '@/api/map/game'
import { keys } from '@/api/hooks/shared/keys'
import { getWorld } from '@/data/world'
import type { GameBucket } from '@/data/types'

export { canLoadSumOnly, plStatementRangeError } from '@/api/map/plStatement'

const GAME_TYPE: Record<string, string> = {
  'SportsBook - Bookmaker': 'Bookmaker',
  Fancy: 'Session',
}

export function liveBody(query: PlStatementQuery, page: number, pageSize: number) {
  const body: Record<string, unknown> = {
    fromDate: query.range.from.slice(0, 10),
    toDate: query.range.to.slice(0, 10),
    page,
    pageSize,
  }
  if (query.q?.trim()) body.q = query.q.trim()
  if (query.tms) body.tms = query.tms
  if (query.name?._id) body.name = query.name
  if (query.memberIds?.length) body.playerName = query.memberIds
  if (query.subGame) body.subGame = query.subGame
  else if (query.sports?.length === 1) body.subGame = query.sports[0]
  if (query.game && query.game !== 'all') body.game = query.game
  if (query.eventName) body.eventName = query.eventName
  if (query.typeName) body.typeName = query.typeName
  else if (query.games?.length === 1) {
    const mapped = GAME_TYPE[query.games[0]!]
    if (mapped) body.typeName = mapped
  }
  // Lab Account.js — staff Market access rewrites typeName before SQL.
  if (query.bookmakerSessionFlag && query.bookmakerSessionFlag !== 'all') {
    body.bookmakerSessionFlag = query.bookmakerSessionFlag
  }
  return body
}

/** Filter body for SumOnly — same Lab fields as the table, without page. */
export function sumOnlyBody(query: PlStatementQuery) {
  const body = liveBody(query, 1, DEFAULT_PAGE_SIZE)
  delete body.page
  delete body.pageSize
  return { ...body, includeCount: true }
}

export async function loadPlStatement(query: PlStatementQuery): Promise<PlStatementResult> {
  const pageSize = query.pageSize ?? DEFAULT_PAGE_SIZE
  const page = query.page ?? 1

  const rangeErr = plStatementRangeError(query.range)
  if (rangeErr) {
    return unavailablePlStatement({ ...query, page, pageSize }, rangeErr)
  }

  if (hasLiveApi()) {
    try {
      const body = liveBody(query, page, pageSize)
      const live = await apiFetch<GetDownlineAccountsDataResponse>(
        '/v1/account/getDownlineAccountsData',
        { method: 'POST', body: JSON.stringify(body) },
      )
      return fromDownlineAccountsData(live, { ...query, page, pageSize })
    } catch (err) {
      if (isUnreachableApiError(err)) {
        return unavailablePlStatement({ ...query, page, pageSize })
      }
      const message = err instanceof Error ? err.message : 'Request failed'
      return unavailablePlStatement({ ...query, page, pageSize }, message)
    }
  }

  const catalogName = FIXTURE_GAMES.find((row) => row._id === query.game)?.name
  const catalogGame =
    catalogName === 'SportsBook' ||
    catalogName === 'Live Casino' ||
    catalogName === 'Card Game'
      ? (catalogName as GameBucket)
      : undefined

  const summary = await api.getPnl({
    ...query,
    page: 1,
    pageSize: 100_000,
    ...(catalogGame ? { games: [catalogGame] } : {}),
  })
  const world = getWorld()
  const memberName = (memberId: string) => world.byId.get(memberId)?.username ?? memberId
  return fromFixturePnl(summary, { ...query, page, pageSize }, memberName)
}

/**
 * Lab Net total amount — `POST /v1/account/getDownlineAccountsSumOnly`.
 * Today → PG; historical → BQ (Lab Mongo vs BigQuery). Cap 93 days.
 */
export async function loadPlStatementSumOnly(
  query: PlStatementQuery,
): Promise<{ netPnl: number; totalRecords?: number } | null> {
  if (!hasLiveApi()) return null
  if (plStatementRangeError(query.range)) return null
  try {
    const response = await apiFetch<GetDownlineAccountsSumOnlyResponse>(
      '/v1/account/getDownlineAccountsSumOnly',
      { method: 'POST', body: JSON.stringify(sumOnlyBody(query)) },
    )
    const netPnl = netFromDownlineSumOnly(response)
    if (netPnl === undefined) return null
    const totalRecords = response.value ? response.data?.totalRecords : undefined
    return {
      netPnl,
      ...(typeof totalRecords === 'number' ? { totalRecords } : {}),
    }
  } catch {
    return null
  }
}

export function usePlStatement(query: PlStatementQuery) {
  return useQuery({
    queryKey: keys.plStatement(query),
    queryFn: () => loadPlStatement(query),
    placeholderData: (previous) => previous,
  })
}

export function usePlStatementSumOnly(query: PlStatementQuery) {
  const sumKey = {
    from: query.range.from.slice(0, 10),
    to: query.range.to.slice(0, 10),
    ...(query.q?.trim() ? { q: query.q.trim() } : {}),
    ...(query.tms ? { tms: query.tms } : {}),
    ...(query.name?._id ? { nameId: query.name._id } : {}),
    ...(query.game && query.game !== 'all' ? { game: query.game } : {}),
    ...(query.subGame ? { subGame: query.subGame } : {}),
    ...(query.eventName ? { eventName: query.eventName } : {}),
    ...(query.typeName ? { typeName: query.typeName } : {}),
    ...(query.bookmakerSessionFlag && query.bookmakerSessionFlag !== 'all'
      ? { bookmakerSessionFlag: query.bookmakerSessionFlag }
      : {}),
    ...(query.memberIds?.length ? { memberIds: query.memberIds } : {}),
  }
  return useQuery({
    queryKey: keys.plStatementSumOnly(sumKey),
    queryFn: () => loadPlStatementSumOnly(query),
    enabled: hasLiveApi(),
    placeholderData: (previous) => previous,
  })
}
